import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Loader2, Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { language, switchLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Password strength calculator
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: '', color: '', text: '' };
    
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    if (score <= 2) {
      return { strength: t('weak'), color: 'bg-rose-500', width: '33%', textColor: 'text-rose-600' };
    } else if (score <= 3) {
      return { strength: t('medium'), color: 'bg-amber-500', width: '66%', textColor: 'text-amber-600' };
    } else {
      return { strength: t('strong'), color: 'bg-emerald-500', width: '100%', textColor: 'text-emerald-600' };
    }
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!name.trim()) {
      setError(t('nameRequired'));
      return;
    }

    if (!email.trim()) {
      setError(t('emailRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('invalidEmail'));
      return;
    }

    if (password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }

    setLoading(true);

    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      let errorMsg = result.error;
      if (errorMsg.includes('already registered') || errorMsg.includes('already exists')) {
        errorMsg = t('emailAlreadyUsed');
      } else if (errorMsg.includes('invalid email')) {
        errorMsg = t('invalidEmail');
      }
      setError(errorMsg);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
        <button
          onClick={() => switchLanguage('fr')}
          className={`px-3 py-1 text-sm rounded transition-all duration-200 ${
            language === 'fr'
              ? 'bg-blue-600 text-white font-medium'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="lang-fr-button"
        >
          FR
        </button>
        <button
          onClick={() => switchLanguage('en')}
          className={`px-3 py-1 text-sm rounded transition-all duration-200 ${
            language === 'en'
              ? 'bg-blue-600 text-white font-medium'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="lang-en-button"
        >
          EN
        </button>
      </div>

      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Search className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <span className="text-2xl font-bold text-slate-900">SafeSourcer</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">{t('createAccount')}</h2>
            <p className="mt-2 text-sm md:text-base text-slate-600">{t('createAccountSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">{t('fullName')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {password && (
                <div className="space-y-1 mt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      {t('passwordStrength')}
                    </span>
                    <span className={`font-medium ${passwordStrength.textColor}`}>
                      {passwordStrength.strength}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: passwordStrength.width }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('creatingAccount')}
                </>
              ) : (
                t('createAccount')
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200" data-testid="login-link">
              {t('login')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden md:block auth-split-bg"></div>
    </div>
  );
};

export default Register;
